import { ArrowLeft, type LucideIcon } from 'lucide-react';
import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { Badge } from '../Badge/Badge';
import { Icon } from '../Icon/Icon';
import s from './AppShell.module.css';

/* ---------- AppShell ---------- */

export type AppShellProps = {
  /** Phones/tablets: sticky top bar. Hidden from lg (the sidebar takes over). */
  topBar?: ReactNode;
  /** Phones/tablets: fixed bottom nav. Hidden from lg. */
  bottomNav?: ReactNode;
  /** From lg: left sidebar. */
  sidebar?: ReactNode;
  children: ReactNode;
  /** Rendered before the top bar, for a skip link: it must be the first thing in the tab order. */
  beforeTopBar?: ReactNode;
};

/** Mobile-first app frame: top bar + bottom nav on phones, sidebar from lg. Handles safe areas. */
export function AppShell({ beforeTopBar, topBar, bottomNav, sidebar, children }: AppShellProps) {
  return (
    <div className={s.shell} data-has-bottom-nav={bottomNav ? true : undefined}>
      {/* A skip link has to come before everything it skips, in DOM order. */}
      {beforeTopBar}
      {sidebar ? <aside className={s.sidebar}>{sidebar}</aside> : null}
      <div className={s.column}>
        {topBar ? <div className={s.topBarSlot}>{topBar}</div> : null}
        <main className={s.main} id="main" tabIndex={-1}>
          {children}
        </main>
      </div>
      {bottomNav ? <div className={s.bottomNavSlot}>{bottomNav}</div> : null}
    </div>
  );
}

/* ---------- TopBar ---------- */

export type TopBarProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  /** A back control, e.g. <TopBarBack link={<Link to=".." />} />. */
  back?: ReactNode;
  /** Trailing actions (notifications bell, avatar menu). */
  actions?: ReactNode;
};

export function TopBar({ title, subtitle, back, actions }: TopBarProps) {
  return (
    <header className={s.topBar}>
      {back}
      <div className={s.titles}>
        <span className={s.title}>{title}</span>
        {subtitle ? <span className={s.subtitle}>{subtitle}</span> : null}
      </div>
      {actions ? <div className={s.actions}>{actions}</div> : null}
    </header>
  );
}

/** Back control: pass a router `link` (e.g. <Link to=".." />), or an `onClick`. */
export function TopBarBack({
  link,
  label = 'Back',
  onClick,
}: {
  link?: ReactElement<{ className?: string; children?: ReactNode }>;
  label?: string;
  onClick?: () => void;
}) {
  const icon = <Icon icon={ArrowLeft} size="lg" />;
  if (link && isValidElement(link))
    return cloneElement(link, { className: s.back, 'aria-label': label, children: icon } as object);
  return (
    <button type="button" className={s.back} aria-label={label} onClick={onClick}>
      {icon}
    </button>
  );
}

/* ---------- Nav items (shared) ---------- */

type NavItemProps = {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  /** Count badge (e.g. unbought gifts). */
  badge?: number;
  badgeLabel?: string;
  /** A router link to render as the item, e.g. <NavLink to="/home" />. Its children are replaced. */
  link?: ReactElement<{ className?: string; children?: ReactNode }>;
  href?: string;
};

/** Render `link` (or an <a href>) with our class, aria-current and content. */
function renderLink({
  link,
  href,
  active,
  className,
  content,
}: {
  link?: NavItemProps['link'];
  href?: string;
  active?: boolean;
  className: string | undefined;
  content: ReactNode;
}) {
  const props = {
    className,
    'aria-current': active ? ('page' as const) : undefined,
    'data-active': active || undefined,
  };
  if (link && isValidElement(link))
    return cloneElement(link, { ...props, children: content } as object);
  return (
    <a href={href} {...props}>
      {content}
    </a>
  );
}

/* ---------- Bottom nav ---------- */

export function BottomNav({ label = 'Main', children }: { label?: string; children: ReactNode }) {
  return (
    <nav className={s.bottomNav} aria-label={label}>
      <ul className={s.bottomList}>{children}</ul>
    </nav>
  );
}

export function BottomNavItem({
  icon,
  label,
  active,
  badge,
  badgeLabel,
  link,
  href,
}: NavItemProps) {
  const content = (
    <>
      <span className={s.bottomIcon}>
        <Icon icon={icon} size="lg" />
        {badge ? <Badge count={badge} label={badgeLabel} className={s.navBadge} /> : null}
      </span>
      <span>{label}</span>
    </>
  );
  return (
    <li className={s.bottomItem}>
      {renderLink({ link, href, active, className: s.bottomLink, content })}
    </li>
  );
}

/* ---------- Sidebar nav (lg+) ---------- */

export function SidebarNav({
  header,
  footer,
  label = 'Main',
  children,
}: {
  header?: ReactNode;
  footer?: ReactNode;
  label?: string;
  children: ReactNode;
}) {
  return (
    <div className={s.sidebarInner}>
      {header ? <div className={s.sidebarHeader}>{header}</div> : null}
      <nav aria-label={label}>
        <ul className={s.sideList}>{children}</ul>
      </nav>
      {footer ? <div className={s.sidebarFooter}>{footer}</div> : null}
    </div>
  );
}

export function SidebarNavItem({
  icon,
  label,
  active,
  badge,
  badgeLabel,
  link,
  href,
}: NavItemProps) {
  const content = (
    <>
      <Icon icon={icon} size="md" />
      <span className={s.sideLabel}>{label}</span>
      {badge ? <Badge count={badge} label={badgeLabel} /> : null}
    </>
  );
  return <li>{renderLink({ link, href, active, className: s.sideLink, content })}</li>;
}
